import { useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import type { MRT_Row, MRT_TableInstance } from "mantine-react-table-open";
import { mastrDataQueries } from "../../../../hooks/useMastrData";
import { UNNAMED_OBJECT_NAME } from "../../../../utils/constants";
import {
  DATE_DISPLAY_FORMAT,
  DATE_TIME_DISPLAY_FORMAT
} from "../../../../utils/dates/dates.constants";
import { ObjectName } from "../../../../utils/enums";
import { meteringServiceProviderQueries } from "../../../AdminDashboard/MeteringServiceProviderTable/hooks/useMeteringServiceProvider";
import type { ConfiguratorComponent } from "../ComponentTable";

interface CollectDataForExportProps {
  mrtTable: MRT_TableInstance<ConfiguratorComponent>;
  objectName: ObjectName;
}

function useComponentTableExport() {
  const queryClient = useQueryClient();

  async function collectDataForExport({
    mrtTable,
    objectName
  }: CollectDataForExportProps): Promise<Array<Array<string>>> {
    const columnIdsForExport = mrtTable
      .getAllColumns()
      .filter((col) => !(col.id?.includes("mrt-") || col.id === "icon"))
      .map((col) => col.id);

    const dataRows: Array<Array<string>> = await Promise.all(
      Object.values(mrtTable.getRowModel().rowsById).map(async (row) =>
        Promise.all(
          columnIdsForExport.map((key) =>
            valueFormatter({
              key,
              objectName,
              row
            })
          )
        )
      )
    );

    return dataRows;
  }

  async function valueFormatter({
    key,
    objectName,
    row
  }: {
    key: string;
    objectName: ObjectName;
    row: MRT_Row<ConfiguratorComponent>;
  }): Promise<string> {
    switch (key) {
      case "einheit":
        return einheitFormatter(row.getValue<string | null>(key));

      case "name": {
        if (objectName === ObjectName.GhostNode) {
          return row.getValue(key) || UNNAMED_OBJECT_NAME;
        }
        return row.getValue(key);
      }

      case "commissioning":
      case "decommissioning": {
        const date = row.getValue<DateTime | null>(key);
        return date ? date.toFormat(DATE_DISPLAY_FORMAT) : "";
      }

      case "updated": {
        const date = row.getValue<DateTime>(key);
        return date ? date.toFormat(DATE_TIME_DISPLAY_FORMAT) : "";
      }

      case "meteringServiceProvider":
        return meteringServiceProviderFormatter(row.getValue(key));

      default:
        return row.getValue(key);
    }
  }

  async function einheitFormatter(einheitId: string | null): Promise<string> {
    const endpoint = einheitId;

    if (!endpoint || endpoint === null) {
      return "";
    }

    const mastrNumber = await queryClient.fetchQuery(
      mastrDataQueries.detail(endpoint)
    );

    return mastrNumber?.einheitMastrNummer
      ? mastrNumber.einheitMastrNummer
      : "";
  }

  async function meteringServiceProviderFormatter(
    mspId: number | null
  ): Promise<string> {
    if (mspId === null) {
      return "";
    }

    const msp = await queryClient.fetchQuery(
      meteringServiceProviderQueries.detail(mspId)
    );

    if (!msp) {
      return "";
    }

    return msp.name;
  }

  return { collectDataForExport };
}

export { useComponentTableExport };
